import React from 'react';
import { Button } from '@americanexpress/dls-react';
import { IconChevronRight, IconLounge } from '@americanexpress/dls-icons';
import { cn, isCenturionLounge } from '@/helpers/utils';
import { useRouteParams } from '@/context/routeParamsContext';
import { useModal } from '@/context/modalContext';
import { useAnalyticsContext } from '@/context/analyticsContext';
import {
  useLoungeById,
  useLoungeAmenitiesWithLocaleTranslations,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { amenityIdToIconMap } from '@/constants/amenityIconMapping';
import { modalTypes } from '@/components/common/modals/constants';
import HighlightedAmenities from '@/components/details/HighlightedAmenities';

const LoungeDetailsAmenitiesList = () => {
  const { loungeId } = useRouteParams();
  const lounge = useLoungeById(loungeId);
  const amenities = useLoungeAmenitiesWithLocaleTranslations(loungeId);
  const content = useCommonContent();
  const { setModalName } = useModal();
  const { customTrack } = useAnalyticsContext();
  const highlightedAmenities = content?.amenities?.highlightedAmenities;
  const exploreAllAmenities = content?.amenities?.exploreAllAmenities;
  const shouldShowAmenityDetails = Boolean(
    isCenturionLounge(lounge) &&
      amenities.length > 0 &&
      exploreAllAmenities?.buttonText
  );

  const handleExploreAllAmenities = (event) => {
    customTrack({ event, detail: 'explore all amenities' });
    setModalName(modalTypes.AMENITIES_MODAL);
  };

  const handleHighlightedAmenity = (event, amenityTitle) => {
    customTrack({
      event,
      detail: `${amenityTitle.toLowerCase()} highlighted amenity`,
    });
    setModalName(modalTypes.AMENITIES_MODAL);
  };

  return (
    <div className="margin-0-b">
      <h2 className="margin-1-b tw-heading-xs-bold tw-text-gray-900">
        {content?.amenities?.header}
      </h2>
      {shouldShowAmenityDetails && highlightedAmenities && (
        <HighlightedAmenities
          highlightedAmenities={highlightedAmenities}
          onOpenModal={handleHighlightedAmenity}
        />
      )}
      <ul
        id="lounge-overview-amenity-list"
        data-qe-id="standard-amenities"
        className={cn(
          'pad-0-lr tw-mt-4 tw-grid tw-grid-cols-1 sm_tw-grid-cols-2'
        )}
      >
        {amenities.map((amenity) => {
          const TheIcon = amenityIdToIconMap[amenity.id] || IconLounge;

          return (
            <li
              key={amenity.id}
              data-qe-id={`standard-amenity-${amenity.id
                .toLowerCase()
                .replaceAll('_', '-')}`}
              className={cn(
                'leading-4 ps-0 pe-0 flex tw-mb-6 tw-mr-6 svg-children_tw-fill-gray-800',
                amenity.id
              )}
            >
              <div>
                <TheIcon
                  role="img"
                  aria-hidden="true"
                  className="tw-mr-2"
                  color="inherit"
                  size="md"
                />
              </div>
              {amenity.label}
            </li>
          );
        })}
      </ul>
      {shouldShowAmenityDetails && (
        <Button
          id="explore-all-amenities"
          data-qe-id="explore-all-amenities"
          variant="tertiary"
          className="pad-0 tw-min-w-0 tw-border-0 important_tw-min-h-0"
          icon={
            <IconChevronRight
              data-qe-id="explore-all-amenities-chevron"
              color="inherit"
              size="sm"
            />
          }
          iconPosition="end"
          reactlytics={{ disable: true }}
          onClick={handleExploreAllAmenities}
        >
          {exploreAllAmenities.buttonText}
        </Button>
      )}
    </div>
  );
};

export default LoungeDetailsAmenitiesList;
